import { createContext, useState, useContext, useEffect, type ReactNode } from 'react';
import { getPublicTenants } from '@/api/public';
import { useAuth } from '@/contexts/AuthContext';
import type { Tenant } from '@/types';

const TENANT_STORAGE_KEY = 'selected_tenant_id';

const getStoredTenantId = (): string => {
  try {
    return localStorage.getItem(TENANT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

const storeTenantId = (id: string): void => {
  try {
    localStorage.setItem(TENANT_STORAGE_KEY, id);
  } catch (error) {
    console.error('Failed to store tenant ID:', error);
  }
};

export interface TenantContextValue {
  tenants: Tenant[];
  selectedTenantId: string;
  setTenantId: (id: string) => void;
  tenantId: number | null;
  currentTenant: Tenant | null;
  isLoadingTenants: boolean;
  // True when the active tenant is locked to the user's own (non-super-admin).
  // UI surfaces (TenantSwitcher) use this to disable cross-tenant selection.
  isTenantLocked: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser, isSuperAdmin } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(getStoredTenantId);
  const [isLoadingTenants, setIsLoadingTenants] = useState<boolean>(true);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const data = (await getPublicTenants()) as Tenant[] | undefined;
        setTenants(data || []);
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  // Non-super-admins are locked to their own tenant. If localStorage had a
  // stale "All FPAs" or another tenant from a prior session/account, force
  // it back to the user's own as soon as auth resolves. Super-admins keep
  // whatever they chose.
  const isTenantLocked =
    Boolean(currentUser) &&
    !isSuperAdmin() &&
    currentUser?.tenant_id != null;

  useEffect(() => {
    if (!isTenantLocked) return;
    const ownId = String(currentUser?.tenant_id ?? '');
    if (ownId && selectedTenantId !== ownId) {
      setSelectedTenantId(ownId);
      storeTenantId(ownId);
    }
  }, [isTenantLocked, currentUser?.tenant_id, selectedTenantId]);

  const setTenantId = (id: string): void => {
    if (isTenantLocked) {
      // Silently ignore — the UI shouldn't be offering this affordance to
      // a locked user in the first place, but defense in depth.
      return;
    }
    setSelectedTenantId(id);
    storeTenantId(id);
  };

  const tenantId: number | null = selectedTenantId ? Number(selectedTenantId) : null;

  const currentTenant: Tenant | null = tenantId
    ? tenants.find((t) => Number(t.id) === tenantId) ?? null
    : null;

  const value: TenantContextValue = {
    tenants,
    selectedTenantId,
    setTenantId,
    tenantId,
    currentTenant,
    isLoadingTenants,
    isTenantLocked,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextValue => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
